import axios from 'axios'
import { authContext } from 'contexts/'
import { IAuthContext } from 'interfaces/utils.interface'

import { useCallback, useContext, useEffect, useState } from 'react'

let fetching_new_token = false
let request_to_refresh: Function[] = []

const getBaseApiUrl = () => {
  return process.env.REACT_APP_BASE_API_URL
}

export const useJwtToken = () => {
  const auth = useContext(authContext)

  const [invalidRefreshToken, setInvalidRefreshToken] = useState(false)
  const invalidRefreshTokenCallBack = useCallback(() => setInvalidRefreshToken(true), [])

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const accessToken = auth?.getAccessToken()
        if (!!accessToken && !isAuthUrl(config.url ?? '')) {
          config.headers = { ...config.headers, Authorization: `${process.env.REACT_APP_JWT_TOKEN_PREFIX} ${accessToken}` }
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        return response
      },
      (error) => {
        const originalReq = error.config
        if (!axios.isCancel(error) && error.response && error.response.status === 401 && originalReq.headers.Authorization) {
          return refreshTokenAndRetry(auth!, error, invalidRefreshTokenCallBack)
        }
        return Promise.reject(error)
      },
    )

    return () => {
      axios.interceptors.response.eject(responseInterceptor)
      axios.interceptors.request.eject(requestInterceptor)
    }
  }, [])

  return invalidRefreshToken
}

//refresh token and retry the request
const refreshTokenAndRetry = (auth: IAuthContext, error: any, invalidRefreshTokenCallBack?: () => void) => {
  try {
    const { response: errorResponse } = error

    /*We create a new Promise that will retry the request,
      clone all the request configuration from the failed
      request in the error object. */
    const retryOriginalRequest = new Promise((resolve, reject) => {
      /* We need to add the request retry to the queue
      since there another request that already attempt to
      refresh the token */
      addSubscriber((new_access_token: string) => {
        if (!new_access_token) reject(null)
        errorResponse.config.headers.Authorization = `${process.env.REACT_APP_JWT_TOKEN_PREFIX} ${new_access_token}`
        resolve(axios(errorResponse.config))
      })
    })

    if (!fetching_new_token) {
      fetching_new_token = true
      refreshToken(auth.getRefreshToken() ?? '', auth.saveTokens, auth.removeTokens)
        .then(() => {
          const new_token = auth?.getAccessToken()!
          if (new_token) {
            fetching_new_token = false
            onAccessTokenFetched(new_token)
          }
        })
        .catch((error) => {
          if (error.response.status === 401) {
            fetching_new_token = false
            clearRequestToRefresh()
            auth.removeTokens()
            !!invalidRefreshTokenCallBack && invalidRefreshTokenCallBack()
          }
        })
    }
    return retryOriginalRequest
  } catch (err) {
    return Promise.reject(err)
  }
}

const refreshToken = async (
  refreshToken: string,
  successCallback: (token: string, refreshToken: string, changeToken: string) => void,
  errorCallback: () => void,
) => {
  const headers = {
    'Content-Type': 'application/json',
  }

  try {
    const response = await axios.post(
      `${getBaseApiUrl()}/Account/RefreshToken`,
      {
        refreshToken,
      },
      {
        headers: headers,
      },
    )

    if (response.data.accessToken) {
      successCallback(response.data.accessToken, response.data.refreshToken, response.data.changeToken)
      return response
    } else {
      errorCallback()
      const error = (response.data && response.data.message) || response.statusText
      return Promise.reject(error)
    }
  } catch (error) {
    return Promise.reject(error)
  }
}

const clearRequestToRefresh = () => {
  while (request_to_refresh.length) {
    request_to_refresh.pop()
  }
}

const onAccessTokenFetched = (accessToken: string) => {
  // When the refresh is successful, we start retrying the requests one by one and empty the queue
  request_to_refresh.forEach((callback: Function) => callback(accessToken))
  request_to_refresh = []
}

const addSubscriber = (callback: Function) => {
  request_to_refresh.push(callback)
}

const isAuthUrl = (url: string) => {
  return url.toLowerCase().indexOf('/login') >= 0 || url.toLowerCase().indexOf('/refreshtoken') >= 0
}
